import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash2, Loader2, Tag, User, Filter, ArrowLeft, Edit3, Building2, Mail, Phone, Play } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORES = ['#7c3aed', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#84cc16', '#64748b'];

export default function BandejaContactosEtiquetasPage() {
  const [tab, setTab] = useState('contactos'); // contactos | etiquetas | reglas

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/admin/bandeja-unificada" className="text-sm text-purple-600 hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Volver a la bandeja
        </Link>
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Contactos, Etiquetas y Reglas</h1>
      <p className="text-sm text-gray-500 mb-6">Gestioná contactos externos, etiquetas personalizadas y reglas de etiquetado automático.</p>

      <div className="flex gap-2 border-b mb-6 overflow-x-auto">
        {[
          { v: 'contactos', label: 'Contactos externos', icon: User },
          { v: 'etiquetas', label: 'Etiquetas', icon: Tag },
          { v: 'reglas', label: 'Reglas de etiquetado', icon: Filter },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-2 ${tab === t.v ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${t.v}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'contactos' && <ContactosSection />}
      {tab === 'etiquetas' && <EtiquetasSection />}
      {tab === 'reglas' && <ReglasSection />}
    </div>
  );
}

// ══════════════════════ CONTACTOS EXTERNOS ══════════════════════
function ContactosSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dlg, setDlg] = useState(null); // null | 'nuevo' | contactoObj

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const url = q ? `${API}/bandeja-unificada/contactos-externos?q=${encodeURIComponent(q)}` : `${API}/bandeja-unificada/contactos-externos`;
      const r = await axios.get(url);
      setItems(r.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [q]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar contacto "${c.nombre}"?`)) return;
    try {
      await axios.delete(`${API}/bandeja-unificada/contactos-externos/${c.id}`);
      fetchList();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input
          placeholder="Buscar por nombre, email u organización…"
          value={q}
          onChange={e => setQ(e.target.value)}
          className="max-w-md"
          data-testid="contactos-buscar"
        />
        <Button onClick={() => setDlg('nuevo')} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-nuevo-contacto">
          <Plus className="w-4 h-4 mr-1" /> Nuevo contacto
        </Button>
      </div>

      {loading ? <Loader2 className="animate-spin w-6 h-6 text-purple-600" /> : (
        items.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Sin contactos externos guardados. Creá uno para que su ficha aparezca automáticamente en la bandeja.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {items.map(c => (
              <Card key={c.id} data-testid={`contacto-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 truncate">{c.nombre}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1 truncate"><Mail className="w-3.5 h-3.5" /> {c.email}</p>
                      {c.organizacion && <p className="text-sm text-gray-500 flex items-center gap-1 truncate"><Building2 className="w-3.5 h-3.5" /> {c.organizacion}</p>}
                      {c.telefono && <p className="text-sm text-gray-500 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.telefono}</p>}
                      {c.notas && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{c.notas}</p>}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant="outline" onClick={() => setDlg(c)} data-testid={`btn-editar-contacto-${c.id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => eliminar(c)} className="text-red-600 hover:text-red-700" data-testid={`btn-borrar-contacto-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      {dlg && (
        <ContactoDialog
          contacto={dlg === 'nuevo' ? null : dlg}
          onClose={() => setDlg(null)}
          onSaved={() => { setDlg(null); fetchList(); }}
        />
      )}
    </div>
  );
}

function ContactoDialog({ contacto, onClose, onSaved }) {
  const [form, setForm] = useState(contacto || { nombre: '', email: '', organizacion: '', telefono: '', notas: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const guardar = async () => {
    setSaving(true); setError('');
    try {
      if (contacto?.id) {
        await axios.patch(`${API}/bandeja-unificada/contactos-externos/${contacto.id}`, form);
      } else {
        await axios.post(`${API}/bandeja-unificada/contactos-externos`, form);
      }
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Error al guardar'); }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="dialog-contacto">
        <DialogHeader>
          <DialogTitle>{contacto?.id ? 'Editar contacto' : 'Nuevo contacto externo'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nombre *</Label>
            <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="input-contacto-nombre" />
          </div>
          <div>
            <Label className="text-xs">Email *</Label>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="input-contacto-email" />
          </div>
          <div>
            <Label className="text-xs">Organización</Label>
            <Input value={form.organizacion || ''} onChange={e => setForm({ ...form, organizacion: e.target.value })} data-testid="input-contacto-organizacion" />
          </div>
          <div>
            <Label className="text-xs">Teléfono</Label>
            <Input value={form.telefono || ''} onChange={e => setForm({ ...form, telefono: e.target.value })} data-testid="input-contacto-telefono" />
          </div>
          <div>
            <Label className="text-xs">Notas</Label>
            <textarea
              className="w-full border rounded p-2 text-sm"
              rows={3}
              value={form.notas || ''}
              onChange={e => setForm({ ...form, notas: e.target.value })}
              data-testid="input-contacto-notas"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-guardar-contacto">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════ ETIQUETAS ══════════════════════
function EtiquetasSection() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bandeja-unificada/etiquetas`);
      setTags(r.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const eliminar = async (t) => {
    if (!window.confirm(`¿Eliminar la etiqueta "${t.nombre}"? Se quitará de todas las conversaciones y reglas.`)) return;
    try {
      await axios.delete(`${API}/bandeja-unificada/etiquetas/${t.id}`);
      fetchList();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div>
      <Button onClick={() => setDlg('nuevo')} className="bg-purple-600 hover:bg-purple-700 mb-4" data-testid="btn-nueva-etiqueta">
        <Plus className="w-4 h-4 mr-1" /> Nueva etiqueta
      </Button>
      {loading ? <Loader2 className="animate-spin w-6 h-6 text-purple-600" /> : (
        tags.length === 0 ? <p className="text-sm text-gray-500 italic">Sin etiquetas creadas.</p> : (
          <div className="flex flex-wrap gap-2">
            {tags.map(t => (
              <div
                key={t.id}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border"
                style={{ background: `${t.color}22`, color: t.color, borderColor: `${t.color}55` }}
                data-testid={`etiqueta-${t.id}`}
              >
                <Tag className="w-3.5 h-3.5" />
                <span>{t.nombre}</span>
                <button onClick={() => setDlg(t)} className="hover:opacity-70" title="Editar">
                  <Edit3 className="w-3 h-3" />
                </button>
                <button onClick={() => eliminar(t)} className="hover:opacity-70" title="Eliminar">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )
      )}
      {dlg && (
        <EtiquetaDialog
          etiqueta={dlg === 'nuevo' ? null : dlg}
          onClose={() => setDlg(null)}
          onSaved={() => { setDlg(null); fetchList(); }}
        />
      )}
    </div>
  );
}

function EtiquetaDialog({ etiqueta, onClose, onSaved }) {
  const [nombre, setNombre] = useState(etiqueta?.nombre || '');
  const [color, setColor] = useState(etiqueta?.color || COLORES[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const guardar = async () => {
    setSaving(true); setError('');
    try {
      if (etiqueta?.id) {
        await axios.patch(`${API}/bandeja-unificada/etiquetas/${etiqueta.id}`, { nombre, color });
      } else {
        await axios.post(`${API}/bandeja-unificada/etiquetas`, { nombre, color });
      }
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="dialog-etiqueta">
        <DialogHeader>
          <DialogTitle>{etiqueta?.id ? 'Editar etiqueta' : 'Nueva etiqueta'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nombre *</Label>
            <Input value={nombre} onChange={e => setNombre(e.target.value)} data-testid="input-etiqueta-nombre" />
          </div>
          <div>
            <Label className="text-xs">Color</Label>
            <div className="flex gap-2 flex-wrap">
              {COLORES.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 ${color === c ? 'border-gray-900 ring-2 ring-purple-200' : 'border-gray-200'}`}
                  style={{ background: c }}
                  data-testid={`color-${c}`}
                />
              ))}
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-guardar-etiqueta">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════ REGLAS DE ETIQUETADO ══════════════════════
function ReglasSection() {
  const [reglas, setReglas] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/bandeja-unificada/reglas-etiquetado`),
        axios.get(`${API}/bandeja-unificada/etiquetas`),
      ]);
      setReglas(r1.data || []);
      setTags(r2.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const eliminar = async (r) => {
    if (!window.confirm('¿Eliminar esta regla?')) return;
    try {
      await axios.delete(`${API}/bandeja-unificada/reglas-etiquetado/${r.id}`);
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const [aplicando, setAplicando] = useState(null);
  const aplicarAExistentes = async (r) => {
    if (!window.confirm(`Aplicar la regla a la bandeja actual: se etiquetarán todos los mails de "${r.valor}" con la etiqueta "${r.etiqueta_nombre}". ¿Continuar?`)) return;
    setAplicando(r.id);
    try {
      const res = await axios.post(`${API}/bandeja-unificada/reglas-etiquetado/${r.id}/aplicar-a-existentes`);
      alert(`Listo. ${res.data.convs_actualizadas} conversaciones etiquetadas.`);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setAplicando(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-600">Cuando entre un email desde un remitente o dominio configurado, se aplicará automáticamente la etiqueta correspondiente.</p>
        <Button onClick={() => setDlg(true)} disabled={tags.length === 0} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-nueva-regla">
          <Plus className="w-4 h-4 mr-1" /> Nueva regla
        </Button>
      </div>
      {tags.length === 0 && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">Primero creá al menos una etiqueta.</p>}

      {loading ? <Loader2 className="animate-spin w-6 h-6 text-purple-600" /> : (
        reglas.length === 0 ? <p className="text-sm text-gray-500 italic">Sin reglas configuradas.</p> : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs">
                <tr>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-left p-2">Valor</th>
                  <th className="text-left p-2">Etiqueta</th>
                  <th className="text-left p-2">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {reglas.map(r => (
                  <tr key={r.id} data-testid={`regla-${r.id}`}>
                    <td className="p-2">
                      {r.tipo === 'asunto_contiene' ? 'Asunto contiene' : r.tipo === 'dominio' ? 'Dominio' : 'Email'}
                    </td>
                    <td className="p-2 font-mono text-xs">{r.valor}</td>
                    <td className="p-2">
                      {r.etiqueta_nombre ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ background: `${r.etiqueta_color}22`, color: r.etiqueta_color }}
                        >
                          <Tag className="w-3 h-3" />{r.etiqueta_nombre}
                        </span>
                      ) : <span className="text-red-500 text-xs">(etiqueta borrada)</span>}
                    </td>
                    <td className="p-2">{r.activa ? <span className="text-green-700 text-xs">Activa</span> : <span className="text-gray-400 text-xs">Inactiva</span>}</td>
                    <td className="p-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => aplicarAExistentes(r)}
                          disabled={aplicando === r.id || !r.etiqueta_nombre}
                          title="Aplicar esta regla a los correos que ya están en la bandeja"
                          data-testid={`btn-aplicar-existentes-${r.id}`}
                        >
                          {aplicando === r.id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <><Play className="w-3.5 h-3.5 mr-1" /> <span className="hidden sm:inline text-xs">Aplicar ahora</span></>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => eliminar(r)} className="text-red-600" data-testid={`btn-borrar-regla-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {dlg && (
        <ReglaDialog
          tags={tags}
          onClose={() => setDlg(false)}
          onSaved={() => { setDlg(false); fetchAll(); }}
        />
      )}
    </div>
  );
}

function ReglaDialog({ tags, onClose, onSaved }) {
  const [tipo, setTipo] = useState('dominio');
  const [valor, setValor] = useState('');
  const [etiquetaId, setEtiquetaId] = useState(tags[0]?.id || '');
  const [aplicarExistentes, setAplicarExistentes] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const guardar = async () => {
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/bandeja-unificada/reglas-etiquetado`, {
        tipo, valor, etiqueta_id: etiquetaId, activa: true,
        aplicar_a_existentes: aplicarExistentes,
      });
      if (aplicarExistentes && r.data?.convs_actualizadas > 0) {
        alert(`Regla creada. Se etiquetaron ${r.data.convs_actualizadas} conversaciones existentes.`);
      }
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="dialog-regla">
        <DialogHeader><DialogTitle>Nueva regla de etiquetado</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <select className="w-full border rounded p-2 text-sm" value={tipo} onChange={e => setTipo(e.target.value)} data-testid="select-regla-tipo">
              <option value="dominio">Dominio (ej: colegio.com)</option>
              <option value="email">Email exacto (ej: juan@empresa.com)</option>
              <option value="asunto_contiene">Asunto contiene (ej: "factura")</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Valor</Label>
            <Input
              value={valor}
              onChange={e => setValor(e.target.value)}
              placeholder={
                tipo === 'email' ? 'usuario@dominio.com'
                : tipo === 'dominio' ? 'dominio.com'
                : 'palabra o frase a buscar en el asunto'
              }
              data-testid="input-regla-valor"
            />
          </div>
          <div>
            <Label className="text-xs">Etiqueta a aplicar</Label>
            <select className="w-full border rounded p-2 text-sm" value={etiquetaId} onChange={e => setEtiquetaId(e.target.value)} data-testid="select-regla-etiqueta">
              {tags.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer bg-purple-50 border border-purple-200 rounded p-2">
            <input
              type="checkbox"
              checked={aplicarExistentes}
              onChange={e => setAplicarExistentes(e.target.checked)}
              className="mt-0.5"
              data-testid="check-aplicar-existentes"
            />
            <span>
              <strong className="text-purple-800">Aplicar a la bandeja actual</strong>
              <br />
              <span className="text-xs text-gray-600">Etiquetá también los correos que ya llegaron y matcheen esta regla.</span>
            </span>
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving || !valor || !etiquetaId} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-guardar-regla">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
